import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { RootState } from '../index';
import { getPersistorRef } from '../persistorRef';
import { showSnackbar } from '../snackbarSlice';
import { esOtraCuenta } from '../../features/auth/cuentaDelToken';

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4000/api/v2';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const auth = (getState() as RootState).auth;
    if (auth.accessToken) headers.set('Authorization', `Bearer ${auth.accessToken}`);
    // Staff de plataforma "entrando" a un tenant → scopea el request a ese tenant.
    if (auth.actingTenant?.id) headers.set('X-Acting-Tenant', auth.actingTenant.id);
    return headers;
  },
  credentials: 'include', // para enviar la cookie del refresh token
});

// Mutex: evita que múltiples requests simultáneas con 401 hagan refresh en paralelo.
// La primera inicia el refresh; las demás esperan esa misma promesa.
let refreshPromise: Promise<boolean> | null = null;

/** Re-intenta con refresh token si recibe 401 */
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  // Suscripción suspendida → el backend bloquea las escrituras con 402. Avisamos con un
  // snackbar (cubre cualquier acción de crear/editar que el usuario intente).
  if (result.error?.status === 402 && (result.error.data as any)?.error === 'subscription_suspended') {
    api.dispatch(showSnackbar({
      message: 'Tu suscripción está suspendida — el CRM está en solo lectura. Regularizá el pago para crear o editar.',
      severity: 'warning',
    }));
  }

  if (result.error?.status === 401) {
    // Si no había sesión CRM activa, devolver el error tal cual — no redirigir.
    // Evita que un 401 de un endpoint público (ej. login de postor con credenciales erróneas)
    // dispare el flujo de refresh y redirija al /login del CRM.
    const state = (api.getState() as import('../index').RootState).auth;
    if (!state.isAuthenticated && !state.accessToken) {
      return result;
    }
    if (!refreshPromise) {
      refreshPromise = (async () => {
        const refreshResult = await rawBaseQuery({ url: '/auth/refresh', method: 'POST' }, api, extraOptions);
        if (refreshResult.data) {
          const { accessToken } = (refreshResult.data as { data: { accessToken: string } }).data;
          const mostrado = (api.getState() as RootState).auth.user?._id;
          api.dispatch({ type: 'auth/tokenRefreshed', payload: accessToken });
          // La renovación trajo OTRA cuenta (se inició sesión con otra en este navegador, o
          // venció un "ingresar como"): se recarga para que la pantalla muestre a quien de
          // verdad está respondiendo el backend. Ver features/auth/cuentaDelToken.
          if (esOtraCuenta(accessToken, mostrado)) {
            await getPersistorRef()?.flush();
            window.location.reload();
            return false;
          }
          return true;
        }
        // Solo un 401 del refresh significa "sesión realmente inválida". Un error de
        // red, un 5xx o un deploy en curso son TRANSITORIOS: NO deslogueamos (el
        // agente estaba escribiendo una nota y perdía todo — bug reportado varias
        // veces). El request original falla y el próximo intento vuelve a refrescar.
        const status = (refreshResult.error as { status?: number | string } | undefined)?.status;
        if (status !== 401) return false;
        api.dispatch(showSnackbar({ message: 'Tu sesión expiró. Iniciá sesión nuevamente.', severity: 'warning' }));
        api.dispatch({ type: 'auth/logout' });
        api.dispatch(baseApi.util.resetApiState());
        await getPersistorRef()?.flush();
        window.location.href = '/login';
        return false;
      })().finally(() => { refreshPromise = null; });
    }

    const refreshed = await refreshPromise;
    if (refreshed) {
      result = await rawBaseQuery(args, api, extraOptions);
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['PaymentLinks', 'User', 'Office', 'Customer', 'Property', 'CalendarEvent', 'Commission', 'Competition', 'BlogPost', 'BlogComment', 'Captacion', 'Tasacion', 'Course', 'CourseProgress', 'FeatureFlag', 'HomeLayout', 'AuditLog', 'Chat', 'ChatMessages', 'Mortgage', 'Auction', 'Notification', 'GustavoConversation', 'PlatformIssue', 'BugReport', 'Credits', 'MarketplaceProduct', 'MarketplaceOrder', 'MarketplaceSlots', 'Wallet', 'WalletTransactions', 'WithdrawAccounts', 'WithdrawalRequests', 'AdminWallets', 'AdminWithdrawals', 'Infocasas', 'BlindLink', 'ExternalClosing', 'RentalCase', 'SaleCase', 'WebContent', 'MyMemories', 'ErrorTriage', 'ToolFlag', 'GustavoRule', 'GustavoPrompt', 'GustavoQCConfig', 'GustavoHallucination', 'GustavoGolden', 'NegativeFeedback', 'Payment', 'SavedCard', 'SubscriptionPlan', 'Subscription', 'InvestmentCatalog', 'InvestmentPackage', 'OperationFee', 'AgentRating', 'Survey', 'SurveyActive', 'PropertyPool', 'TenantBranding', 'TenantSite', 'PhylaxEscalation', 'PhylaxConversation', 'PhylaxConvHistory', 'PhylaxEvent', 'PhylaxCustomer', 'PhylaxAnalytics', 'Application', 'LessonComment', 'CourseRating', 'StudioJob', 'StudioOverview', 'Coupon', 'RentalInventory', 'DailyIndicator', 'ReporteComercial', 'NotifPrefs', 'TodayAgenda', 'PaymentSchedule', 'ExternalListing', 'ListingHealth', 'CaptureProfile', 'GustavoIncident', 'QualityFinding', 'GustavoNotices', 'GustavoManual', 'Task', 'AgentNotes', 'VisitasSinCerrar', 'ContactLead', 'DatosDeCobro', 'MiPortfolio', 'Anuncio', 'AnuncioPendiente', 'AnuncioMetricas', 'MeliPreguntas', 'MeliProspectos', 'LiveSession', 'Evento', 'Seguimiento', 'ChatCall', 'RedesCampanias', 'ValidacionContable', 'ComprobantesDeIngreso', 'PhylaxPlan', 'PhylaxConfig', 'EstudioReferencias', 'EstudioPiezas'],
  endpoints: () => ({}),
});
